import React, { useContext, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import {
  IProduct,
  IProductsQuery,
  ProductsDocument,
  useDeleteProductMutation,
  useProductsQuery
} from '../../generated/graphql';
import { canUserCreate } from '../../utils/rightUtils';
import { Types } from '../../enums/TypeEnums';
import { useNavigate } from 'react-router-dom';
import OverviewTable, { IHeadCell } from '../Common/Tables/OverviewTable';
import NewProduct from './NewProduct';

const ProductList: React.FunctionComponent = () => {
  const history = useNavigate();
  const { dispatch, state } = useContext(AppContext);
  const [openProductForm, setOpenProductForm] = useState(false);
  const [deleteProduct] = useDeleteProductMutation();
  const { data, loading } = useProductsQuery();
  const canCreate = canUserCreate(state.user, Types.Product);

  const openFormMethod = () => {
    setOpenProductForm(true);
  };

  const handleDetails = (rowData: IProduct) => {
    history(`/products/${rowData.id}`, { state: { ...rowData } });
  };

  const remove = (rowData: IProduct) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.name}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (rowData: IProduct) => {
    if (!rowData.id) {
      return;
    }
    dispatch({ type: Actions.HideConfirmation });
    deleteProduct({
      variables: {
        id: parseInt(rowData.id)
      },
      update(cache) {
        const productQuery = cache.readQuery({
          query: ProductsDocument
        }) as IProductsQuery;
        if (productQuery.result) {
          const cacheProductIndex = productQuery.result.findIndex((c) => c?.id === rowData.id);
          if (cacheProductIndex > -1) {
            const data = productQuery.result.filter(function (el) {
              return el?.id !== rowData.id;
            });
            cache.writeQuery({
              query: ProductsDocument,
              data: { result: data }
            });
          }
        }

        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `Product deleted ${rowData.name}`, severity: 'success' }
        });
      }
    }).catch((error) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: { text: error.message, severity: 'error' }
      });
    });
  };

  const columns: IHeadCell[] = [
    {
      title: 'Details',
      width: 63,
      sorting: false
    },
    {
      title: 'ID',
      field: 'id',
      type: 'numeric',
      width: 71
    },
    {
      title: 'Name',
      field: 'name',
      width: 800
    },
    {
      title: 'Product Code',
      field: 'product_code.name',
      width: 171
    },
    {
      title: 'Price',
      field: 'price',
      type: 'numeric',
      width: 192
    },
    {
      title: 'Belgian VAT',
      field: 'vat',
      type: 'numeric',
      width: 149
    },
    {
      title: 'Accounting Code',
      field: 'membership_type.name',
      width: 277
    },
    {
      title: 'Remove',
      width: 93,
      sorting: false
    }
  ];

  return (
    <div className="overviewPage">
      <div className="print\:w-full">
        <OverviewTable
          columns={columns}
          data={data?.result || []}
          loading={loading}
          handleDetails={handleDetails}
          handleRemove={remove}
          pageSize={15}
          createButtonProps={{
            id: 'create-product-button',
            onClick: openFormMethod,
            text: 'Create Product',
            disabled: !canCreate
          }}
        />
        {openProductForm && <NewProduct handleClose={() => setOpenProductForm(false)} />}
      </div>
    </div>
  );
};

export default ProductList;
